import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import PrizeBanner from '../components/PrizeBanner';
import { Trophy, SoccerBall, Lock, CalendarBlank, FloppyDisk, CaretLeft, CaretRight } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WCMatchPredictionsPage = () => {
  const { t } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [matchDays, setMatchDays] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [activeTab, setActiveTab] = useState('predictions');
  const [selectedLeague, setSelectedLeague] = useState('global');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'leaderboard') fetchLeaderboard();
  }, [activeTab, selectedLeague]);

  const fetchData = async () => {
    try {
      const [daysRes, predsRes, leaguesRes] = await Promise.all([
        axios.get(`${API}/wc-lms/match-days`),
        axios.get(`${API}/wc-match-predictions`),
        axios.get(`${API}/leagues`),
      ]);

      setMatchDays(daysRes.data);
      setLeagues(leaguesRes.data);

      const predMap = {};
      for (const p of predsRes.data) {
        predMap[p.match_id] = {
          home: p.predicted_home_score,
          away: p.predicted_away_score,
          points: p.points_earned,
        };
      }
      setPredictions(predMap);

      if (daysRes.data.length > 0 && !selectedDay) {
        const firstDay = daysRes.data[0].date;
        setSelectedDay(firstDay);
        await fetchMatchesForDay(firstDay);
      }
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchesForDay = async (day) => {
    try {
      const res = await axios.get(`${API}/wc-lms/matches?lms_round=${day}`);
      setMatches(res.data);
    } catch {
      setMatches([]);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const url = selectedLeague === 'global'
        ? `${API}/wc-match-predictions/leaderboard`
        : `${API}/wc-match-predictions/leaderboard?league_id=${selectedLeague}`;
      const res = await axios.get(url);
      setLeaderboard(res.data);
    } catch {
      setLeaderboard([]);
    }
  };

  const handleDaySelect = async (day) => {
    setSelectedDay(day);
    await fetchMatchesForDay(day);
  };

  const handleScoreChange = (matchId, side, value) => {
    const num = value === '' ? '' : Math.max(0, Math.min(99, parseInt(value) || 0));
    setPredictions(prev => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [side]: num,
        points: prev[matchId]?.points || 0,
      }
    }));
  };

  const isMatchLocked = (match) => {
    const now = new Date();
    const matchDt = new Date(`${match.match_date}T${match.match_time || '00:00'}:00Z`);
    return now >= matchDt;
  };

  const handleSave = async () => {
    const dayPredictions = matches
      .filter(m => !isMatchLocked(m))
      .filter(m => predictions[m.id]?.home !== undefined && predictions[m.id]?.home !== '' &&
                   predictions[m.id]?.away !== undefined && predictions[m.id]?.away !== '')
      .map(m => ({
        match_id: m.id,
        predicted_home_score: parseInt(predictions[m.id].home),
        predicted_away_score: parseInt(predictions[m.id].away),
      }));

    if (dayPredictions.length === 0) {
      toast.error(t('wcMatch.noPredictions'));
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/wc-match-predictions`, dayPredictions);
      toast.success(t('wcMatch.saved', { count: res.data.saved }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const dayIndex = matchDays.findIndex(d => d.date === selectedDay);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <p className="text-text-muted text-center">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-white flex items-center gap-3" data-testid="wc-match-predictions-title">
            <SoccerBall size={36} weight="fill" className="text-primary" />
            {t('wcMatch.title')}
          </h1>
          <p className="text-text-secondary text-base mt-1">{t('wcMatch.subtitle')}</p>
        </div>

        <div className="mb-6">
          <PrizeBanner variant="compact" />
        </div>

        {/* Scoring Info */}
        <div className="bg-background-paper border border-border rounded-sm p-4 mb-6 flex flex-wrap gap-6" data-testid="scoring-info">
          <div className="flex items-center gap-2">
            <span className="bg-primary text-black font-black text-sm px-2 py-0.5 rounded">10</span>
            <span className="text-text-secondary text-sm">{t('wcMatch.exactScore')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-primary/60 text-black font-black text-sm px-2 py-0.5 rounded">3</span>
            <span className="text-text-secondary text-sm">{t('wcMatch.correctResult')}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('predictions')}
            data-testid="tab-match-predictions"
            className={`px-4 py-2 rounded-sm font-bold uppercase text-sm tracking-wide transition-all ${activeTab === 'predictions' ? 'bg-primary text-black' : 'bg-background-paper text-text-secondary border border-border hover:border-primary'}`}
          >
            {t('wcMatch.predictions')}
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            data-testid="tab-match-leaderboard"
            className={`px-4 py-2 rounded-sm font-bold uppercase text-sm tracking-wide transition-all ${activeTab === 'leaderboard' ? 'bg-primary text-black' : 'bg-background-paper text-text-secondary border border-border hover:border-primary'}`}
          >
            {t('wcMatch.leaderboard')}
          </button>
        </div>

        {activeTab === 'predictions' && (
          <>
            {/* Day Navigation */}
            <div className="flex items-center gap-2 mb-6">
              <button
                onClick={() => dayIndex > 0 && handleDaySelect(matchDays[dayIndex - 1].date)}
                disabled={dayIndex <= 0}
                data-testid="prev-day-btn"
                className="p-2 bg-background-paper border border-border rounded-sm hover:border-primary disabled:opacity-30 transition-all"
              >
                <CaretLeft size={20} className="text-white" />
              </button>

              <div className="flex-1 overflow-x-auto">
                <div className="flex gap-1.5 min-w-max px-1">
                  {matchDays.map((day) => (
                    <button
                      key={day.date}
                      onClick={() => handleDaySelect(day.date)}
                      data-testid={`day-${day.date}`}
                      className={`px-3 py-2 rounded-sm text-xs font-bold uppercase whitespace-nowrap transition-all ${
                        selectedDay === day.date
                          ? 'bg-primary text-black'
                          : 'bg-background-paper text-text-secondary border border-border hover:border-primary'
                      }`}
                    >
                      {new Date(day.date + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      <span className="ml-1 text-[10px] opacity-70">({day.match_count})</span>
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => dayIndex < matchDays.length - 1 && handleDaySelect(matchDays[dayIndex + 1].date)}
                disabled={dayIndex >= matchDays.length - 1}
                data-testid="next-day-btn"
                className="p-2 bg-background-paper border border-border rounded-sm hover:border-primary disabled:opacity-30 transition-all"
              >
                <CaretRight size={20} className="text-white" />
              </button>
            </div>

            {/* Match Cards */}
            <div className="space-y-3 mb-6">
              {matches.map((match) => {
                const locked = isMatchLocked(match);
                const pred = predictions[match.id] || {};
                const completed = match.status === 'completed';
                return (
                  <div
                    key={match.id}
                    data-testid={`match-card-${match.id}`}
                    className={`bg-background-paper border rounded-sm p-4 transition-all ${
                      completed ? 'border-primary/30' : locked ? 'border-border opacity-60' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-text-muted text-xs uppercase tracking-wide">
                        {match.group ? `${t('wcMatch.group')} ${match.group}` : match.stage} · {match.match_time || ''}
                      </span>
                      {locked && <Lock size={14} className="text-text-muted" />}
                      {completed && pred.points !== undefined && (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${pred.points === 10 ? 'bg-primary text-black' : pred.points === 3 ? 'bg-primary/50 text-black' : 'bg-red-500/20 text-red-400'}`}>
                          {pred.points > 0 ? `+${pred.points} pts` : '0 pts'}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      {/* Home Team */}
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {match.home_team?.flag && (
                          <img src={match.home_team.flag} alt="" className="w-7 h-5 object-cover rounded-sm flex-shrink-0" />
                        )}
                        <span className="text-white font-medium text-sm truncate">{match.home_team?.name}</span>
                      </div>

                      {/* Score Inputs */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <input
                          type="number"
                          min="0"
                          max="99"
                          value={pred.home ?? ''}
                          onChange={(e) => handleScoreChange(match.id, 'home', e.target.value)}
                          disabled={locked}
                          data-testid={`score-home-${match.id}`}
                          className="w-10 h-10 text-center bg-background border border-border rounded-sm text-white font-bold text-lg outline-none focus:border-primary disabled:opacity-40 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-text-muted font-bold mx-1">-</span>
                        <input
                          type="number"
                          min="0"
                          max="99"
                          value={pred.away ?? ''}
                          onChange={(e) => handleScoreChange(match.id, 'away', e.target.value)}
                          disabled={locked}
                          data-testid={`score-away-${match.id}`}
                          className="w-10 h-10 text-center bg-background border border-border rounded-sm text-white font-bold text-lg outline-none focus:border-primary disabled:opacity-40 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>

                      {/* Away Team */}
                      <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                        <span className="text-white font-medium text-sm truncate text-right">{match.away_team?.name}</span>
                        {match.away_team?.flag && (
                          <img src={match.away_team.flag} alt="" className="w-7 h-5 object-cover rounded-sm flex-shrink-0" />
                        )}
                      </div>
                    </div>

                    {/* Actual Result (if completed) */}
                    {completed && match.home_score !== null && (
                      <div className="mt-2 text-center">
                        <span className="text-text-muted text-xs">{t('wcMatch.finalScore')}: </span>
                        <span className="text-primary font-bold text-sm">{match.home_score} - {match.away_score}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Save Button */}
            {matches.some(m => !isMatchLocked(m)) && (
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-predictions-btn"
                className="w-full py-4 bg-primary hover:bg-primary-hover text-black font-black uppercase tracking-wide text-lg rounded-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <FloppyDisk size={24} weight="bold" />
                {saving ? t('common.loading') : t('wcMatch.savePredictions')}
              </button>
            )}
          </>
        )}

        {activeTab === 'leaderboard' && (
          <div className="bg-background-paper border border-border rounded-sm overflow-hidden">
            {/* League Filter */}
            <div className="p-4 border-b border-border">
              <select
                value={selectedLeague}
                onChange={(e) => setSelectedLeague(e.target.value)}
                data-testid="league-filter-select"
                className="bg-background border border-border text-white rounded-sm px-3 py-2 outline-none focus:border-primary text-sm"
              >
                <option value="global">{t('leagues.global')}</option>
                {leagues.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>

            {/* Leaderboard Table */}
            <table className="w-full" data-testid="match-predictions-leaderboard">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 px-4 text-left text-text-muted text-xs uppercase">#</th>
                  <th className="py-3 px-4 text-left text-text-muted text-xs uppercase">{t('common.player')}</th>
                  <th className="py-3 px-4 text-right text-text-muted text-xs uppercase">{t('wcMatch.preds')}</th>
                  <th className="py-3 px-4 text-right text-text-muted text-xs uppercase">{t('common.points')}</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.length === 0 ? (
                  <tr><td colSpan={4} className="py-8 text-center text-text-muted">{t('wcMatch.noEntries')}</td></tr>
                ) : (
                  leaderboard.map((entry, i) => (
                    <tr key={entry.user_id} className="border-b border-border/50 hover:bg-background-subtle transition-all" data-testid={`lb-entry-${i}`}>
                      <td className="py-3 px-4 text-primary font-bold">{i + 1}</td>
                      <td className="py-3 px-4 text-white font-medium">{entry.display_name}</td>
                      <td className="py-3 px-4 text-right text-text-secondary">{entry.total_predictions}</td>
                      <td className="py-3 px-4 text-right text-primary font-bold">{entry.total_points}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WCMatchPredictionsPage;
